import type Alpine from 'alpinejs';
import type {StreamAlpine} from './stream';
import {warn} from '../../utils';

type RequestMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
type RequestData = FormData | URLSearchParams | HTMLFormElement | Record<string, unknown> | string | Blob | ArrayBuffer;
type RequestExtra = Record<string, unknown> | FormData | URLSearchParams;
type RequestSource = Element;
type NavigationHistory = 'auto' | 'push' | 'replace';

type RequestOptions = {
    method?: RequestMethod | Lowercase<RequestMethod>;
    cache?: RequestCache;
    data?: RequestData;
    extra?: RequestExtra;
    headers?: HeadersInit;
    requestId?: string;
    signal?: AbortSignal;
    source?: RequestSource;
    submitter?: HTMLElement | null;
};

type PreparedRequest = {
    url: URL;
    method: RequestMethod;
    init: RequestInit;
};

type RequestResult = {
    requestId: string;
    response: Response | null;
    type: 'empty' | 'navigate' | 'redirect' | 'other' | 'aborted' | 'cancelled' | 'unavailable';
    url: string;
    navigation?: {history: NavigationHistory; url: string};
};

type RequestLifecycleDetail = {
    source: Element | Document;
    url: string;
    method: RequestMethod;
    requestInit: RequestInit;
    requestId: string;
    response?: Response | null;
    responseType?: RequestResult['type'];
    error?: unknown;
    controller: AbortController;
    signal: AbortSignal;
};

type BoundAbortSignal = {
    signal: AbortSignal;
    cleanup(): void;
};

const REQUEST_HEADER = 'X-Trellis-UI-Request';
const CLIENT_SESSION_HEADER = 'X-Trellis-Client';
const REQUEST_ID_HEADER = 'X-Trellis-Request-ID';
const NAVIGATE_HEADER = 'X-Trellis-Navigate';
const NAVIGATE_HISTORY_HEADER = 'X-Trellis-Navigate-History';
const CLIENT_STREAM_SELECTOR = '[data-ui-stream][data-ui-stream-client]';
const CLIENT_STREAM_OPEN_EVENT = 'ui:stream:open';
const CLIENT_STREAM_ERROR_EVENT = 'ui:stream:error';
const CLIENT_STREAM_DISCONNECTED_EVENT = 'ui:stream:disconnected';
const CLIENT_STREAM_REFRESH_EVENT = 'ui:stream:refresh';
const CSRF_META_SELECTOR = 'meta[name="csrf-token"]';
const SSE_CONTENT_TYPE = 'text/event-stream';
const PENDING_ATTRIBUTE = 'data-ui-request-pending';
const FORM_URL_ENCODED = 'application/x-www-form-urlencoded';
const FORM_MULTIPART = 'multipart/form-data';
// GET is the only bodyless helper. DELETE remains body-capable for API symmetry;
// Django apps may still prefer POST intents or URL-specific delete routes.
const METHODS_WITHOUT_BODY = new Set<RequestMethod>(['GET']);

let AlpineRuntime: StreamAlpine | null = null;
let navigateResponseHandler: ((result: RequestResult) => boolean) | null = null;

const normalizeMethod = (method: string | null | undefined): RequestMethod => {
    const value = (method || 'GET').toUpperCase();
    if (value === 'POST' || value === 'PUT' || value === 'PATCH' || value === 'DELETE') return value;
    return 'GET';
};

const appendEntry = (target: FormData | URLSearchParams, key: string, value: string | Blob): void => {
    if (target instanceof FormData) {
        target.append(key, value);
        return;
    }
    target.append(key, value instanceof File ? value.name : String(value));
};

const addValue = (target: FormData | URLSearchParams, key: string, value: unknown): void => {
    if (Array.isArray(value)) {
        value.forEach((item) => addValue(target, key, item));
        return;
    }
    if (value === undefined || value === null) return;
    appendEntry(target, key, value instanceof Blob ? value : String(value));
};

const appendRecord = (target: FormData | URLSearchParams, record: Record<string, unknown>): void => {
    Object.entries(record).forEach(([key, value]) => addValue(target, key, value));
};

const appendExtra = (target: FormData | URLSearchParams, extra: RequestExtra | undefined): void => {
    if (!extra) return;
    if (extra instanceof FormData || extra instanceof URLSearchParams) {
        for (const [key, value] of extra.entries()) appendEntry(target, key, value);
        return;
    }
    appendRecord(target, extra);
};

const headersWithDefaults = (headersInit: HeadersInit | undefined, body: BodyInit | null): Headers => {
    const headers = new Headers(headersInit);
    headers.set(REQUEST_HEADER, 'true');

    const csrf = document.querySelector<HTMLMetaElement>(CSRF_META_SELECTOR)?.content;
    if (csrf) headers.set('X-CSRFToken', csrf);

    if (body instanceof URLSearchParams && !headers.has('Content-Type')) {
        headers.set('Content-Type', FORM_URL_ENCODED);
    }

    return headers;
};

const urlFor = (url: string | URL | undefined, source: RequestSource | undefined, submitter: HTMLElement | null): URL => {
    if (url) return new URL(url, document.baseURI);

    if (source instanceof HTMLFormElement) {
        const action = submitter?.getAttribute('formaction') || source.getAttribute('action') || window.location.href;
        return new URL(action, document.baseURI);
    }

    if (source instanceof HTMLAnchorElement) return new URL(source.href, document.baseURI);

    return new URL(window.location.href);
};

const formEnctype = (form: HTMLFormElement, submitter: HTMLElement | null): string => (
    submitter?.getAttribute('formenctype') || form.enctype || FORM_URL_ENCODED
);

const submitterNameValue = (submitter: HTMLElement | null): [string, string] | null => {
    if (!(submitter instanceof HTMLButtonElement || submitter instanceof HTMLInputElement)) return null;
    const name = submitter.getAttribute('name');
    if (!name) return null;
    return [name, submitter.value];
};

const formPayload = (form: HTMLFormElement, submitter: HTMLElement | null): FormData => {
    const payload = new FormData(form);
    const pair = submitterNameValue(submitter);
    if (pair) payload.append(...pair);
    return payload;
};

const paramsFrom = (payload: FormData | URLSearchParams): URLSearchParams => {
    const params = new URLSearchParams();
    for (const [key, value] of payload.entries()) {
        if (value instanceof File) {
            params.append(key, value.name);
        } else {
            params.append(key, value);
        }
    }
    return params;
};

const payloadFromData = (data: RequestData | undefined, natural: FormData | URLSearchParams): {payload: FormData | URLSearchParams | BodyInit | null; explicit: boolean} => {
    if (data === undefined) return {payload: natural, explicit: false};
    if (data instanceof HTMLFormElement) return {payload: new FormData(data), explicit: true};
    if (data instanceof FormData || data instanceof URLSearchParams || typeof data === 'string' || data instanceof Blob || data instanceof ArrayBuffer) {
        return {payload: data, explicit: true};
    }
    const params = new URLSearchParams();
    appendRecord(params, data);
    return {payload: params, explicit: true};
};

const mergePayload = (
    payload: FormData | URLSearchParams | BodyInit | null,
    extra: RequestExtra | undefined,
): FormData | URLSearchParams | BodyInit | null => {
    if (!extra) return payload;

    if (payload instanceof FormData || payload instanceof URLSearchParams) {
        appendExtra(payload, extra);
        return payload;
    }

    const params = new URLSearchParams();
    if (typeof payload === 'string') params.append('data', payload);
    appendExtra(params, extra);
    return params;
};

const naturalPayload = (
    source: RequestSource | undefined,
    submitter: HTMLElement | null,
): {payload: FormData | URLSearchParams; enctype: string} => {
    if (source instanceof HTMLFormElement) {
        return {
            payload: formPayload(source, submitter),
            enctype: formEnctype(source, submitter),
        };
    }
    return {payload: new URLSearchParams(), enctype: FORM_URL_ENCODED};
};

const resolveMethod = (source: RequestSource | undefined, optionMethod: string | undefined, submitter: HTMLElement | null): RequestMethod => {
    if (optionMethod) return normalizeMethod(optionMethod);
    if (source instanceof HTMLFormElement) {
        return normalizeMethod(submitter?.getAttribute('formmethod') || source.getAttribute('method'));
    }
    return 'GET';
};

const prepareRequest = (url: string | URL | undefined, options: RequestOptions = {}): PreparedRequest => {
    const source = options.source;
    const submitter = options.submitter ?? null;
    const method = resolveMethod(source, options.method, submitter);
    const resolvedUrl = urlFor(url, source, submitter);
    const natural = naturalPayload(source, submitter);
    const basePayload = payloadFromData(options.data, natural.payload);
    const payload = mergePayload(basePayload.payload, options.extra);

    const init: RequestInit = {
        method,
        ...(options.cache ? {cache: options.cache} : {}),
    };
    let body: BodyInit | null = null;

    if (METHODS_WITHOUT_BODY.has(method)) {
        if (payload instanceof FormData || payload instanceof URLSearchParams) {
            paramsFrom(payload).forEach((value, key) => resolvedUrl.searchParams.append(key, value));
        } else if (typeof payload === 'string') {
            resolvedUrl.searchParams.append('data', payload);
        }
    } else if (payload instanceof FormData && (basePayload.explicit || natural.enctype === FORM_MULTIPART)) {
        body = payload;
    } else if (payload instanceof FormData) {
        body = paramsFrom(payload);
    } else {
        body = payload;
    }

    init.headers = headersWithDefaults(options.headers, body);
    if (body !== null && body !== undefined) init.body = body;

    return {url: resolvedUrl, method, init};
};

const eventTargetFor = (source: Element | undefined): Element | Document => (
    source?.isConnected ? source : document
);

const lifecycleEvent = (
    name: string,
    detail: RequestLifecycleDetail,
    options: {cancelable?: boolean} = {},
): CustomEvent<RequestLifecycleDetail> => new CustomEvent<RequestLifecycleDetail>(name, {
    bubbles: true,
    composed: true,
    cancelable: options.cancelable ?? false,
    detail,
});

const dispatchLifecycle = (name: string, source: Element | undefined, detail: RequestLifecycleDetail, options?: {cancelable?: boolean}): boolean => (
    eventTargetFor(source).dispatchEvent(lifecycleEvent(name, detail, options))
);

const pendingCounts = new WeakMap<Element, number>();

const setPending = (source: Element | undefined): void => {
    if (!source) return;
    const count = pendingCounts.get(source) ?? 0;
    pendingCounts.set(source, count + 1);
    source.setAttribute('aria-busy', 'true');
    source.setAttribute(PENDING_ATTRIBUTE, 'true');
};

const clearPending = (source: Element | undefined): void => {
    if (!source) return;
    const nextCount = (pendingCounts.get(source) ?? 1) - 1;
    if (nextCount > 0) {
        pendingCounts.set(source, nextCount);
        return;
    }
    pendingCounts.delete(source);
    source.removeAttribute('aria-busy');
    source.removeAttribute(PENDING_ATTRIBUTE);
};

const isSseResponse = (response: Response): boolean => (
    response.headers.get('Content-Type')?.toLowerCase().includes(SSE_CONTENT_TYPE) ?? false
);

const handleResponse = async (response: Response): Promise<Omit<RequestResult, 'requestId'>> => {
    const navigate = response.headers.get(NAVIGATE_HEADER);
    if (navigate) {
        const history = response.headers.get(NAVIGATE_HISTORY_HEADER) ?? 'auto';
        if (history !== 'auto' && history !== 'push' && history !== 'replace') {
            warn('x-ui-request: invalid navigation history', history);
            return {response, type: 'other', url: response.url};
        }
        return {response, type: 'navigate', url: response.url, navigation: {history, url: navigate}};
    }
    if (
        response.status === 204
        || response.body === null
        || response.headers.get('X-Trellis-UI-Response') === 'empty'
    ) return {response, type: 'empty', url: response.url};

    if (response.redirected && !isSseResponse(response)) {
        window.location.assign(response.url);
        return {response, type: 'redirect', url: response.url};
    }

    warn('x-ui-request: expected an empty response', response);
    return {response, type: 'other', url: response.url};
};

type ClientStreamReady = {token: string} | null;
const streamReadiness = new WeakMap<HTMLElement, Promise<ClientStreamReady>>();

const streamClientToken = (stream: HTMLElement): string | null => {
    const token = stream.dataset.uiStreamClient;
    const expiresAt = Number(stream.dataset.uiStreamClientExpires);
    if (!token) return null;
    if (Number.isFinite(expiresAt) && expiresAt > 0 && expiresAt <= Date.now()) return null;
    return token;
};

const readyClientStream = (): ClientStreamReady => {
    const stream = document.querySelector<HTMLElement>(CLIENT_STREAM_SELECTOR);
    const token = stream ? streamClientToken(stream) : null;
    if (!stream || !token || stream.dataset.uiStreamState !== 'open') return null;
    return {token};
};

const streamTimeout = (stream: HTMLElement): number => {
    const configured = Number(stream.dataset.uiStreamTimeout);
    return Number.isFinite(configured) && configured >= 0 ? configured : 5000;
};

const waitForClientStream = (signal: AbortSignal): Promise<ClientStreamReady> => {
    const stream = document.querySelector<HTMLElement>(CLIENT_STREAM_SELECTOR);
    const token = stream ? streamClientToken(stream) : null;
    if (!stream) return Promise.resolve(null);
    if (token && stream.dataset.uiStreamState === 'open') return Promise.resolve({token});
    if (
        stream.dataset.uiStreamState === 'error'
        || stream.dataset.uiStreamState === 'disconnected'
        || signal.aborted
    ) return Promise.resolve(null);

    const existingReadiness = streamReadiness.get(stream);
    const readiness: Promise<ClientStreamReady> = existingReadiness ?? new Promise<ClientStreamReady>((resolve) => {
        const finish = (value: ClientStreamReady): void => {
            window.clearTimeout(timeoutId);
            stream.removeEventListener(CLIENT_STREAM_OPEN_EVENT, onOpen);
            stream.removeEventListener(CLIENT_STREAM_ERROR_EVENT, onError);
            stream.removeEventListener(CLIENT_STREAM_DISCONNECTED_EVENT, onError);
            resolve(value);
        };
        const onOpen = (): void => {
            const currentToken = streamClientToken(stream);
            finish(currentToken ? {token: currentToken} : null);
        };
        const onError = (): void => finish(null);
        const timeoutId = window.setTimeout(onError, streamTimeout(stream));

        stream.addEventListener(CLIENT_STREAM_OPEN_EVENT, onOpen, {once: true});
        stream.addEventListener(CLIENT_STREAM_ERROR_EVENT, onError, {once: true});
        stream.addEventListener(CLIENT_STREAM_DISCONNECTED_EVENT, onError, {once: true});
    }).finally(() => {
        streamReadiness.delete(stream);
    });
    if (!existingReadiness) streamReadiness.set(stream, readiness);

    return new Promise((resolve) => {
        const onAbort = (): void => resolve(null);
        signal.addEventListener('abort', onAbort, {once: true});
        void readiness.then((result) => {
            signal.removeEventListener('abort', onAbort);
            resolve(result);
        });
    });
};

const prepareClientStream = async (signal: AbortSignal): Promise<ClientStreamReady> => {
    const ready = readyClientStream();
    if (ready) return ready;

    const stream = document.querySelector<HTMLElement>(CLIENT_STREAM_SELECTOR);
    if (!stream) return null;
    if (!streamClientToken(stream)) {
        stream.dispatchEvent(new CustomEvent(CLIENT_STREAM_REFRESH_EVENT));
        const refreshed = readyClientStream();
        if (refreshed) return refreshed;
    }
    return waitForClientStream(signal);
};

const bindExternalAbort = (controller: AbortController, externalSignal: AbortSignal | undefined): BoundAbortSignal => {
    if (!externalSignal) {
        return {
            signal: controller.signal,
            cleanup: () => undefined,
        };
    }

    const abort = (): void => controller.abort(externalSignal.reason);
    if (externalSignal.aborted) {
        abort();
    } else {
        externalSignal.addEventListener('abort', abort, {once: true});
    }

    return {
        signal: controller.signal,
        cleanup: () => externalSignal.removeEventListener('abort', abort),
    };
};

const isAbortError = (error: unknown, signal: AbortSignal): boolean => (
    signal.aborted
    || (error instanceof DOMException && error.name === 'AbortError')
    || (error instanceof Error && error.name === 'AbortError')
);

const requestWithAlpine = async (
    AlpineInstance: StreamAlpine,
    url: string | URL | undefined,
    options: RequestOptions = {},
): Promise<RequestResult> => {
    const source = options.source;
    const controller = new AbortController();
    const requestId = options.requestId ?? crypto.randomUUID();
    const prepared = prepareRequest(url, options);
    const boundAbort = bindExternalAbort(controller, options.signal);
    const signal = boundAbort.signal;
    prepared.init.signal = signal;
    (prepared.init.headers as Headers).set(REQUEST_ID_HEADER, requestId);
    const clientStream = readyClientStream() ?? await prepareClientStream(signal);
    if (signal.aborted) {
        boundAbort.cleanup();
        return {requestId, response: null, type: 'aborted', url: prepared.url.toString()};
    }
    if (!clientStream) {
        boundAbort.cleanup();
        return {requestId, response: null, type: signal.aborted ? 'aborted' : 'unavailable', url: prepared.url.toString()};
    }
    (prepared.init.headers as Headers).set(CLIENT_SESSION_HEADER, clientStream.token);

    const detail: RequestLifecycleDetail = {
        source: source ?? document,
        url: prepared.url.toString(),
        method: prepared.method,
        requestInit: prepared.init,
        requestId,
        controller,
        signal,
    };

    if (!dispatchLifecycle('ui:request:before', source, detail, {cancelable: true})) {
        boundAbort.cleanup();
        return {requestId, response: null, type: 'cancelled', url: prepared.url.toString()};
    }

    setPending(source);
    dispatchLifecycle('ui:request:start', source, detail);

    try {
        const response = await fetch(prepared.url.toString(), prepared.init);
        const result = await handleResponse(response);
        detail.response = response;
        detail.responseType = result.type;

        if ((response.ok && result.type !== 'other') || result.type === 'redirect') {
            dispatchLifecycle('ui:request:success', source, detail);
        } else {
            dispatchLifecycle('ui:request:error', source, detail);
        }

        const requestResult = {...result, requestId};
        if (requestResult.navigation) navigateResponseHandler?.(requestResult);
        return requestResult;
    } catch (error) {
        if (isAbortError(error, signal)) {
            detail.responseType = 'aborted';
            return {requestId, response: null, type: 'aborted', url: prepared.url.toString()};
        }

        detail.error = error;
        dispatchLifecycle('ui:request:error', source, detail);
        throw error;
    } finally {
        boundAbort.cleanup();
        clearPending(source);
        dispatchLifecycle('ui:request:finish', source, detail);
    }
};

const requireAlpine = (): StreamAlpine => {
    if (!AlpineRuntime) {
        throw new Error('Trellis request runtime has not been registered with Alpine.');
    }
    return AlpineRuntime;
};

const request = (url: string | URL, options: RequestOptions = {}): Promise<RequestResult> => (
    requestWithAlpine(requireAlpine(), url, options)
);

const get = (url: string | URL, options: RequestOptions = {}): Promise<RequestResult> => (
    request(url, {...options, method: 'GET'})
);

const post = (url: string | URL, options: RequestOptions = {}): Promise<RequestResult> => (
    request(url, {...options, method: 'POST'})
);

const put = (url: string | URL, options: RequestOptions = {}): Promise<RequestResult> => (
    request(url, {...options, method: 'PUT'})
);

const del = (url: string | URL, options: RequestOptions = {}): Promise<RequestResult> => (
    request(url, {...options, method: 'DELETE'})
);

const registerRequestRuntime = (AlpineInstance: Alpine.Alpine | StreamAlpine): void => {
    AlpineRuntime = AlpineInstance as StreamAlpine;
};

const setNavigateResponseHandler = (handler: ((result: RequestResult) => boolean) | null): void => {
    navigateResponseHandler = handler;
};

export {
    del,
    get,
    post,
    put,
    registerRequestRuntime,
    request,
    requestWithAlpine,
    setNavigateResponseHandler,
};
export type {NavigationHistory, RequestLifecycleDetail, RequestMethod, RequestOptions, RequestResult};
